/** @file Reads a bundle from its files: parses the JSON, keeps sheet PNG bytes (the inverse of bundleFiles). */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { checkManifest } from '@zilo/pose3d-export/check/checkManifest';
import { fromUtf8 } from '@zilo/pose3d-export/text/fromUtf8';
import type { BundleSheet, ExportBundle, ExportPuppet } from '@zilo/pose3d-export/types/bundle';

type Files = Record<string, Uint8Array | string>;

function fileOf(files: Files, path: string): Uint8Array | string {
  const content = Object.hasOwn(files, path) ? files[path] : undefined;
  return content ?? throwError('BUNDLE_FILE_MISSING', { file: path }, { path: `/files/${path}`, hint: 'every manifest path must be present' });
}

function jsonOf(files: Files, path: string): unknown {
  const content = fileOf(files, path);
  try {
    return JSON.parse(typeof content === 'string' ? content : fromUtf8(content)) as unknown;
  } catch (e) {
    return throwError('BUNDLE_FORMAT', { file: path, reason: e instanceof Error ? e.message : String(e) }, { path: `/files/${path}` });
  }
}

/**
 * The bundle `files` hold (JSON as strings or UTF-8 bytes): `manifest.json`, the puppet and clips it names, and each
 * sheet with its PNG bytes as `png` when the file is present. A missing manifest, puppet or clip file throws
 * `BUNDLE_FILE_MISSING`, unparsable JSON or a bad manifest `BUNDLE_FORMAT`; the rest is `checkBundle`'s.
 */
export function readBundleFiles(files: Files): ExportBundle {
  const manifest = checkManifest(jsonOf(files, 'manifest.json'));
  const puppet = jsonOf(files, manifest.puppet) as ExportPuppet;
  const clips: Record<string, Clip> = {};
  for (const [name, path] of Object.entries(manifest.clips)) clips[name] = jsonOf(files, path) as Clip;
  const sheets: Record<string, BundleSheet> = {};
  for (const [name, ref] of Object.entries(manifest.sheets)) {
    const png = Object.hasOwn(files, ref.file) ? files[ref.file] : undefined;
    sheets[name] = { name, size: ref.size, ...(png instanceof Uint8Array ? { png } : {}) };
  }
  return { manifest, puppet, clips, sheets };
}
